import { FC } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { ROUTES } from '@constants';
import { useModalState } from '@hooks';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import UploadIcon from '@mui/icons-material/Upload';
import { CompanyType } from '@services';

import { Tabs } from '@molecules/Tabs';
import { Box, Button, Typography } from '@mui/material';
import { CreateAccountModal } from '@organisms';
import { CreateEnterpriseAccountModal } from '@organisms/Modals/CreateEnterpriseAccountModal';

const links = [
  { label: 'Content creators', to: ROUTES.ContentCreators },
  { label: 'Business accounts', to: ROUTES.BusinessAccounts },
  { label: 'Enterprise', to: ROUTES.Enterprise },
];

interface Props {
  title: string;
  hasCreateAccount?: boolean;
  hasAddWhitelist?: boolean;
}

export const AccountsTabs: FC<Props> = ({ title, hasCreateAccount, hasAddWhitelist }) => {
  const [isOpenCreateAcc, , openCreateAccModal, closeCreateAccModal] = useModalState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isContentCreators = pathname.includes(ROUTES.ContentCreators);
  const isBusinessAccounts = pathname.includes(ROUTES.BusinessAccounts);
  const isEnterprise = pathname.includes(ROUTES.Enterprise);

  const shouldShowModal =
    hasCreateAccount &&
    isOpenCreateAcc &&
    (isEnterprise || isContentCreators || isBusinessAccounts);
  const ModalComponent = isEnterprise ? CreateEnterpriseAccountModal : CreateAccountModal;

  const handleOpenWhitelistModal = () => {
    const companyRoute = isBusinessAccounts ? ROUTES.BusinessAccounts : ROUTES.ContentCreators;
    sessionStorage.setItem(
      'add-whitelist-close-path',
      `/${ROUTES.Whitelist}/${companyRoute}${window.location.search || ''}`,
    );
    navigate(`${companyRoute}/${ROUTES.Add}`);
  };

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h4">{title}</Typography>

        <Box display="flex" gap={2}>
          {hasCreateAccount && (
            <Button variant="contained" startIcon={<PersonAddIcon />} onClick={openCreateAccModal}>
              {`Add ${isEnterprise ? 'Enterprise' : 'Business'} Account`}
            </Button>
          )}
          {hasAddWhitelist && (
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={handleOpenWhitelistModal}
            >
              Upload links
            </Button>
          )}
        </Box>
      </Box>

      <Box mt={2.5} mb={3}>
        <Tabs links={links} />
      </Box>

      <Outlet />

      {shouldShowModal && (
        <ModalComponent
          open={isOpenCreateAcc}
          onClose={closeCreateAccModal}
          companyType={CompanyType.Business}
          isBusiness={isBusinessAccounts}
        />
      )}
    </>
  );
};
